// Extracting keywords from printed card text.
//
// Worth doing mechanically because keywords are the purely structural part of a
// card: Assault, Tank, Deflect and the rest are fully specified by rule 800, so
// a card that has them plays correctly without anyone scripting it.
//
// Riot's own card data brackets every keyword and every symbol -- "[Assault 2]",
// "[Equip] [R]", "[3]" -- which makes this a token scan rather than a guess. The
// only real judgement is telling a keyword the card HAS from one it GRANTS:
// "[Shield] [Tank]" means this unit has both, "Give a unit [Shield 3] and
// [Tank]" means it grants them to something else. Riftbound's layout settles it:
// a card's own keywords are printed first, as a leading run, before any prose.
// So we consume from the front and stop at the first token that is not one.

import type { Domain, Keyword, KeywordValues } from './types.ts';

/** Printed spelling -> internal name. Matched case-insensitively. */
const KEYWORD_NAMES: Record<string, Keyword> = {
  accelerate: 'accelerate',
  action: 'action',
  assault: 'assault',
  deathknell: 'deathknell',
  deflect: 'deflect',
  ganking: 'ganking',
  hidden: 'hidden',
  legion: 'legion',
  reaction: 'reaction',
  shield: 'shield',
  tank: 'tank',
  temporary: 'temporary',
  vision: 'vision',
  equip: 'equip',
  'quick-draw': 'quickDraw',
  quickdraw: 'quickDraw',
  repeat: 'repeat',
  weaponmaster: 'weaponmaster',
  ambush: 'ambush',
  hunt: 'hunt',
  level: 'level',
  unique: 'unique',
  backline: 'backline',
  empower: 'empower',
  empowered: 'empowered',
  flow: 'flow',
};

/** Keywords whose X defaults to 1 when omitted (807.1.b.3 and friends). */
const VALUED: ReadonlySet<Keyword> = new Set<Keyword>([
  'assault', 'shield', 'deflect', 'hunt', 'level',
]);

/**
 * Keywords that are followed by a cost, which is printed as bare symbol tokens
 * ("[Equip] [R]", "[Flow] [2]"). Those tokens belong to the keyword, not to the
 * card's rules text, so they are consumed with it.
 */
const COSTED: ReadonlySet<Keyword> = new Set<Keyword>([
  'equip', 'flow', 'repeat', 'empower',
]);

/**
 * Dependent keywords introduce an ability of their own after a dash, which is
 * prose the grammar has to parse. Scanning stops at them so the caller sees the
 * dependent text in `body`.
 */
const DEPENDENT: ReadonlySet<Keyword> = new Set<Keyword>([
  'deathknell', 'legion', 'level', 'empowered',
]);

const DOMAIN_BY_SYMBOL: Record<string, Domain> = {
  r: 'fury', g: 'calm', b: 'mind', o: 'body', p: 'chaos', y: 'order',
};

/**
 * Reminder text is parenthesised, italic, and has no effect on game function
 * (135.2.d.3). It also mentions other keywords -- Quick-Draw's reminder says
 * "This has [Reaction]" -- so it must be removed before anything else is read,
 * or every card would inherit its own explanations.
 */
/**
 * Brackets a leading keyword that was printed as plain text.
 *
 * Riot's data brackets every keyword -- except on three printings where the
 * leading one is set as prose ("Hidden (Hide now...)", "Equip [O] (...)"). The
 * reminder parenthesis is what identifies it, and both reminder strippers
 * flatten that away, so the rewrite has to happen before either of them runs.
 * Only at the very front, and only with a reminder following, so a keyword
 * *word* inside prose -- "give a unit shield" -- is never mistaken for one the
 * card has.
 */
export function bracketLeadingKeyword(text: string): string {
  return text.replace(
    new RegExp(String.raw`^\s*([A-Za-z][A-Za-z'-]*)( \d+)?((?: \[[^\]]+\])*) \(`),
    (whole, word: string, value: string | undefined, cost: string) =>
      (KEYWORD_NAMES[word.toLowerCase()]
        ? `[${word}${value ?? ''}]${cost} (`
        : whole),
  );
}

export function stripReminders(text: string): string {
  text = bracketLeadingKeyword(text);
  let out = '';
  let depth = 0;
  for (const ch of text) {
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (depth === 0) out += ch;
  }
  return out.replace(/\s+/g, ' ').trim();
}

export interface ParsedKeywords {
  keywords: Keyword[];
  values: KeywordValues;
  /** Energy component of a keyword cost, e.g. Equip [2]. */
  costEnergy: number;
  costPower: number;
  /** 414. The [E] in a keyword cost: "[Empower] - [1], [E]". */
  costExhaust: boolean;
  /**
   * 827. A keyword whose price is a choice: "[Empower] - [1] or [O]". Each
   * alternative buys the same ability, so the caller emits one ability per
   * price rather than trying to fold them into a single cost.
   */
  costAlternatives: { energy: number; power: number; domain: Domain | 'any' | null }[];
  /** Domain named in a keyword cost, when one is printed. */
  costDomain: Domain | 'any' | null;
  /**
   * A keyword cost written as words rather than symbols, e.g.
   * "[Empower] - Discard 1". Left as text for the grammar's cost parser, which
   * already knows that vocabulary.
   */
  costText: string;
  /** Text remaining after the leading keyword run: the card's actual prose. */
  body: string;
}

const TOKEN = /^\s*\[([^\]]+)\]/;

export interface KeywordOptions {
  /**
   * Leave dependent keywords in the body instead of consuming them.
   *
   * A dependent keyword gates the text that follows it -- "[Empowered] I have
   * [Deflect]" means the Deflect only applies while empowered. Consuming the
   * marker here and handing the bare sentence to the grammar would compile a
   * card that always has Deflect, which is silently and badly wrong. Callers
   * that go on to parse the body must set this.
   */
  keepDependent?: boolean;
}

export function parseKeywords(
  text: string | null | undefined, opts: KeywordOptions = {},
): ParsedKeywords {
  const result: ParsedKeywords = {
    keywords: [], values: {}, costEnergy: 0, costPower: 0, costDomain: null,
    costExhaust: false, costAlternatives: [], costText: '', body: '',
  };
  if (!text) return result;

  let rest = stripReminders(text);
  // "[>]" marks the ability attached to a keyword; it carries no meaning here.
  rest = rest.replace(/\[>\]/g, ' ').replace(/\s+/g, ' ');

  for (;;) {
    const m = TOKEN.exec(rest);
    if (!m) break;

    const inner = m[1]!.trim();
    // "[Assault 2]" -> name "assault", value 2.
    const parts = /^([A-Za-z][A-Za-z'-]*)(?:\s+(\d+))?$/.exec(inner);
    if (!parts) break;

    const keyword = KEYWORD_NAMES[parts[1]!.toLowerCase()];
    if (!keyword) break;
    // 827. "[Empower] costs of your units here cost [1] less" *names* the
    // keyword rather than carrying it -- the card has no Empower ability of its
    // own. Taking it as one would eat the subject of the sentence.
    if (/^\s*costs?\b/.test(rest.slice(m[0].length))) break;
    // Stop in front of a dependent keyword rather than swallowing it.
    if (opts.keepDependent && DEPENDENT.has(keyword)) break;

    if (!result.keywords.includes(keyword)) result.keywords.push(keyword);
    if (parts[2] !== undefined) result.values[keyword] = Number(parts[2]);
    else if (VALUED.has(keyword) && result.values[keyword] === undefined) {
      result.values[keyword] = 1;
    }

    rest = rest.slice(m[0].length).trimStart();

    // Some printings set the cost off with a dash -- "[Empower] - [1], [E]".
    // Only skipped when a symbol token actually follows, because the same dash
    // introduces a *worded* cost ("[Empower] - Discard 1") that the block below
    // reads with the dash still in place.
    const dashed = COSTED.has(keyword)
      && /^[-–—]\s*\[(?:\d+|[aerpgboy])\]/i.test(rest);

    // Consume the symbol tokens that make up this keyword's cost.
    if (COSTED.has(keyword)) {
      if (dashed) rest = rest.replace(/^[-–—]\s*/, '');
      for (;;) {
        // "[1], [E]" separates the parts of one price with a comma -- but only
        // in the dashed form. Without the dash a comma ends the keyword and
        // starts an ability of its own ("[Equip] [R], [E]: ..."), and eating
        // across it would charge the Equip for the ability's exhaust.
        const next = dashed ? rest.replace(/^,\s*/, '') : rest;
        const cost = TOKEN.exec(next);
        if (!cost) break;
        const symbol = cost[1]!.trim().toLowerCase();
        if (/^\d+$/.test(symbol)) result.costEnergy += Number(symbol);
        else if (symbol === 'a') { result.costPower += 1; result.costDomain = 'any'; }
        else if (symbol === 'e' && dashed) result.costExhaust = true;
        else if (DOMAIN_BY_SYMBOL[symbol]) {
          result.costPower += 1;
          result.costDomain = DOMAIN_BY_SYMBOL[symbol]!;
        } else break;
        rest = next.slice(cost[0].length).trimStart();
      }
    }

    // A costed keyword can spell its cost out instead: "[Empower] - Discard 1",
    // and the cost can be compound: "[Equip] - [P], Recycle 2 cards from your
    // trash". Everything after the dash is the cost -- the reminder text is
    // already stripped, and a keyword's own effect is a separate sentence -- so
    // the whole remainder is handed on for the grammar to judge rather than
    // matched against a fixed list of shapes here.
    // 827. "[Empower] - [1] or [O]" prints two prices, either of which buys
      // the same ability. Recorded separately from the first, which the fields
      // above already hold.
      while (/^or\s*\[/.test(rest)) {
        rest = rest.replace(/^or\s*/, '');
        const alt = { energy: 0, power: 0, domain: null as Domain | 'any' | null };
        for (;;) {
          const extra = TOKEN.exec(rest);
          if (!extra) break;
          const symbol = extra[1]!.trim().toLowerCase();
          if (/^\d+$/.test(symbol)) alt.energy += Number(symbol);
          else if (symbol === 'a') { alt.power += 1; alt.domain = 'any'; }
          else if (DOMAIN_BY_SYMBOL[symbol]) {
            alt.power += 1;
            alt.domain = DOMAIN_BY_SYMBOL[symbol]!;
          } else break;
          rest = rest.slice(extra[0].length).trimStart();
        }
        if (alt.energy === 0 && alt.power === 0) break;
        result.costAlternatives.push(alt);
      }

    // 820. "[Repeat] - [1] / [A] / [1][A]" prints a *tier* of prices, each one
    // repeating the spell once more. Only one cost can be recorded, so the
    // first is kept and the rest are consumed here -- left in the body they
    // reach the grammar as a stray run of symbols and fail the card.
    if (COSTED.has(keyword) && dashed) {
      while (/^\/\s*/.test(rest)) {
        rest = rest.replace(/^\/\s*/, '');
        for (;;) {
          const extra = TOKEN.exec(rest);
          if (!extra) break;
          const symbol = extra[1]!.trim().toLowerCase();
          if (!/^\d+$/.test(symbol) && symbol !== 'a' && symbol !== 'e'
            && !DOMAIN_BY_SYMBOL[symbol]) break;
          rest = rest.slice(extra[0].length).trimStart();
        }
      }
    }

    // In the dashed form the symbols above may have taken only the first half
    // of a compound price -- "[Equip] - [P], Recycle 2 cards from your trash" --
    // so the worded tail is still read even though symbols were found.
    if (COSTED.has(keyword)
      && (dashed
        || (result.costEnergy === 0 && result.costPower === 0 && !result.costExhaust))) {
      // Anchored to the end of the sentence: the text after the dash is the
      // cost only when there is nothing else in it. "[Empower] - Discard a
      // spell. When I become [Empowered], ..." puts the effect in the same
      // run, and taking all of it would hand the grammar an effect to read as
      // a price.
      // The cost ends at the end of the sentence or at the next bracketed
      // keyword: "[Empower] - Kill a friendly unit [Empowered] I have +2 [M]"
      // is a price followed by a second keyword clause, not one long price.
      const whole = /^[-–—,]\s*((?:\[[^\]]+\]\s*)*,?\s*(?:recycle \d+ cards? from your trash|kill an? [a-z' ]+?|exhaust an? [a-z' ]+?|spend \d+ xp|discard \d+|discard an? [a-z' -]+?|spend my buff|kill me|exhaust me))\s*(?=\[|\bwhen\b|\bif\b|\bwhile\b|$)/i
        .exec(rest);
      const worded = whole ?? /^[-–—,]\s*(discard \d+|recycle \d+ from your trash|spend \d+ xp|kill me|exhaust me|spend my buff)/i
        .exec(rest);
      if (worded) {
        result.costText = worded[1]!.trim().toLowerCase();
        rest = rest.slice(worded[0].length).trimStart();
      }
    }

    // Skip the separator before whatever follows.
    rest = rest.replace(/^[-–—,]\s*/, '').replace(/^and\s+/i, '').trimStart();

    // A dependent keyword is followed by its own effect text.
    if (DEPENDENT.has(keyword)) break;
  }

  result.body = rest.trim();
  return result;
}

/**
 * True when a card's prose carries rules we do not implement yet. Keyword-only
 * cards play correctly from the keyword table alone; everything else needs a
 * compiled or scripted effect, and this is what the coverage report counts.
 */
export function hasProse(body: string): boolean {
  // "Might +0" is the printed Might Bonus on equipment, not rules text (137).
  const cleaned = body.replace(/might\s*[+-]\d+/gi, ' ').trim();
  return /[A-Za-z]{3}/.test(cleaned);
}

/** 143.2: a unit is Mighty while it has 5 or more Might. */
export const MIGHTY_THRESHOLD = 5;
